(() => {
  if (customElements.get('marame-figma-header')) return;
  class MarameFigmaHeader extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.resize?.disconnect();
      this.cartObserver?.disconnect();
      this.tickerResize?.disconnect();
      this.menu = this.querySelector('.marame-figma-menu');
      this.trigger = this.querySelector('[data-marame-menu-open]');
      this.home = this.dataset.home === 'true';
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.initSearch(options);
      this.trigger?.addEventListener('click', () => this.openMenu(), options);
      this.initLocalization();
      this.querySelector('[data-marame-menu-close]')?.addEventListener('click', () => this.closeMenu(), options);
      this.menu?.addEventListener('click', (event) => {
        if (event.target !== this.menu) return;
        const box = this.menu.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.closeMenu();
      }, options);
      this.menu?.addEventListener('close', () => this.afterClose(), options);
      this.menu?.addEventListener('cancel', (event) => {
        event.preventDefault();
        const search = this.menu.querySelector('details-modal');
        if (search?.isOpen()) { search.close(); document.body.classList.add('overflow-hidden'); }
        else this.closeMenu();
      }, options);
      this.menu?.addEventListener('toggle', (event) => {
        const active = event.target;
        if (!active.matches('.marame-figma-menu__group') || !active.open) return;
        this.menu.querySelectorAll('.marame-figma-menu__group[open]').forEach((group) => {
          if (group !== active) group.open = false;
        });
      }, { ...options, capture: true });
      this.addEventListener('open', (event) => {
        if (!event.target.matches?.('shopify-account')) return;
        event.target.style.setProperty('--account-dialog-top', `${Math.round(event.target.getBoundingClientRect().bottom)}px`);
      }, { ...options, capture: true });
      this.schedule = () => {
        if (options.signal.aborted || this.frame) return;
        this.frame = requestAnimationFrame(() => {
          if (options.signal.aborted) return;
          this.frame = 0;
          this.updateTone();
          this.updateImmersive();
        });
      };
      this.resize = new ResizeObserver((entries) => {
        if (options.signal.aborted) return;
        const entry = entries.find(({ target }) => target === this);
        const box = entry?.borderBoxSize?.[0] || entry?.borderBoxSize;
        this.updateHeight(box?.blockSize);
      });
      this.resize.observe(this, { box: 'border-box' });
      if (this.home || this.dataset.pageProfile === 'fragrances') window.addEventListener('scroll', this.schedule, { ...options, passive: true });
      window.addEventListener('resize', this.schedule, { ...options, passive: true });
      document.addEventListener('shopify:section:load', this.schedule, options);
      document.addEventListener('marame:immersive-change', () => this.updateImmersive(), options);
      this.addEventListener('focusin', () => this.updateImmersive(), options);
      this.addEventListener('focusout', this.schedule, options);
      this.cartObserver = new MutationObserver(() => this.syncCartCount());
      const bubble = this.querySelector('#cart-icon-bubble');
      if (bubble) this.cartObserver.observe(bubble, { childList: true, subtree: true });
      this.syncCartCount();
      this.updateTone();
      this.updateImmersive();
      this.initTicker();
    }
    initLocalization() {
      this.querySelectorAll('[data-marame-localization-select]').forEach((select) => {
        if (select.marameLocalizationBootstrap) return;
        const form = select.form;
        if (!form) return;
        const fallback = form.querySelector('[data-marame-localization-submit]');
        const submit = () => {
          try {
            if (typeof form.requestSubmit === 'function') form.requestSubmit();
            else HTMLFormElement.prototype.submit.call(form);
          } catch (_) {
            if (fallback) fallback.hidden = false;
          }
        };
        try {
          select.addEventListener('change', submit);
          select.marameLocalizationBootstrap = submit;
          if (fallback) fallback.hidden = true;
        } catch (_) {
          if (fallback) fallback.hidden = false;
        }
      });
    }
    initSearch(options) {
      this.searchTrigger = this.querySelector('[data-header-search-open]');
      this.searchForm = this.querySelector('[data-header-search-form]');
      this.searchInput = this.querySelector('[data-header-search-input]');
      if (!this.searchTrigger || !this.searchForm || !this.searchInput) return;
      this.searchTrigger.setAttribute('role', 'button');
      this.searchTrigger.setAttribute('aria-controls', this.searchForm.id);
      this.searchTrigger.setAttribute('aria-expanded', 'false');
      this.searchTrigger.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.openSearch();
      }, options);
      this.searchTrigger.addEventListener('keydown', (event) => {
        if (event.key !== ' ') return;
        event.preventDefault();
        this.openSearch();
      }, options);
      this.querySelector('[data-header-search-close]')?.addEventListener('click', () => this.closeSearch(), options);
      this.searchForm.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation();
        this.closeSearch();
      }, options);
      this.searchForm.addEventListener('submit', (event) => {
        this.searchInput.value = this.searchInput.value.trim();
        if (!this.searchInput.value) {
          event.preventDefault();
          this.searchInput.focus({ preventScroll: true });
        }
      }, options);
      document.addEventListener('pointerdown', (event) => {
        if (this.dataset.searchOpen === 'true' && !this.searchForm.contains(event.target) && !this.searchTrigger.contains(event.target)) this.closeSearch(false);
      }, options);
      this.searchForm.addEventListener('focusout', (event) => {
        if (this.searchForm.contains(event.relatedTarget)) return;
        queueMicrotask(() => {
          if (!options.signal.aborted && this.dataset.searchOpen === 'true' && !this.searchForm.contains(document.activeElement)) this.closeSearch(false);
        });
      }, options);
    }
    openSearch() {
      if (!this.searchForm) return;
      this.searchForm.hidden = false;
      this.dataset.searchOpen = 'true';
      this.searchTrigger.setAttribute('aria-expanded', 'true');
      this.searchInput.focus({ preventScroll: true });
    }
    closeSearch(restoreFocus = true) {
      if (!this.searchForm || this.searchForm.hidden) return;
      this.searchForm.hidden = true;
      delete this.dataset.searchOpen;
      this.searchTrigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus) this.searchTrigger.focus({ preventScroll: true });
    }
    initTicker() {
      this.ticker = this.querySelector('.marame-figma-ticker');
      const halves = this.ticker?.querySelectorAll('.marame-figma-ticker__half');
      if (!halves || halves.length !== 2) return;
      this.tickerHalves = Array.from(halves);
      let original = halves[0].querySelector('[data-marame-ticker-original]');
      if (!original) {
        const items = [];
        for (const item of halves[0].children) {
          if (item.classList.contains('marame-figma-ticker__repeat')) break;
          items.push(item);
        }
        if (!items.length) return;
        original = document.createElement('span');
        original.className = 'marame-figma-ticker__repeat';
        original.setAttribute('data-marame-ticker-original', '');
        original.append(...items);
      }
      // Preserve the actual links and editor attributes only on the original group.
      halves[0].replaceChildren(original);
      halves[1].replaceChildren();
      halves[1].setAttribute('aria-hidden', 'true');
      halves[1].inert = true;
      this.tickerOriginal = original;
      this.tickerTemplate = original.cloneNode(true);
      [this.tickerTemplate, ...this.tickerTemplate.querySelectorAll('*')].forEach((element) => {
        Array.from(element.attributes).forEach(({ name }) => {
          if (name === 'id' || name.startsWith('data-shopify-editor-') || name === 'data-marame-ticker-original') element.removeAttribute(name);
        });
      });
      this.tickerTemplate.setAttribute('aria-hidden', 'true');
      this.tickerTemplate.inert = true;
      this.tickerCopies = 0;
      const signal = this.controller.signal;
      this.scheduleTicker = () => {
        if (signal.aborted || this.tickerFrame) return;
        this.tickerFrame = requestAnimationFrame(() => {
          this.tickerFrame = 0;
          if (!signal.aborted) this.fillTicker();
        });
      };
      this.tickerWidth = NaN;
      this.tickerItemWidth = NaN;
      this.tickerResize = new ResizeObserver((entries) => {
        if (signal.aborted) return;
        for (const entry of entries) {
          const box = entry.borderBoxSize?.[0] || entry.borderBoxSize;
          const width = box?.inlineSize ?? entry.target.getBoundingClientRect().width;
          if (entry.target === this.ticker) this.tickerWidth = width;
          if (entry.target === this.tickerOriginal) this.tickerItemWidth = width;
        }
        this.fillTicker();
      });
      this.tickerResize.observe(this.ticker, { box: 'border-box' });
      this.tickerResize.observe(original, { box: 'border-box' });
      document.fonts?.addEventListener('loadingdone', this.scheduleTicker, { signal });
      document.fonts?.ready.then(this.scheduleTicker);
    }
    fillTicker() {
      const width = this.tickerWidth;
      const itemWidth = this.tickerItemWidth;
      if (!Number.isFinite(width) || !Number.isFinite(itemWidth) || width <= 0 || itemWidth < 1) return;
      const copies = Math.max(4, Math.ceil(width / itemWidth));
      if (copies === this.tickerCopies) return;
      this.tickerHalves.forEach((half) => {
        while (half.children.length > copies) half.lastElementChild.remove();
        while (half.children.length < copies) half.append(this.tickerTemplate.cloneNode(true));
      });
      this.tickerCopies = copies;
    }
    updateHeight(observedHeight) {
      const height = Math.round((observedHeight ?? this.getBoundingClientRect().height) * 100) / 100;
      if (!(height > 0)) return;
      const style = document.documentElement.style;
      const value = `${height}px`;
      if (style.getPropertyValue('--marame-header-height') !== value) style.setProperty('--marame-header-height', value);
      if (style.getPropertyValue('--header-height') !== value) style.setProperty('--header-height', value);
    }
    setDataState(key, value) {
      const text = String(value);
      if (this.dataset[key] !== text) this.dataset[key] = text;
    }
    updateTone() {
      if (this.dataset.pageProfile === 'fragrances') { this.setDataState('scrolled', (window.scrollY || document.documentElement.scrollTop || 0) > 10); return; }
      if (!this.home) return;
      const top = window.scrollY || document.documentElement.scrollTop || 0;
      this.setDataState('atTop', top < 24);
      const headerRect = this.getBoundingClientRect();
      const footer = document.querySelector('[data-marame-figma-footer]');
      const footerSection = footer?.closest('.shopify-section') || footer;
      const footerRect = footerSection?.getBoundingClientRect();
      const overFooter = Boolean(footerRect && footerRect.top <= headerRect.bottom && footerRect.bottom > headerRect.top);
      this.setDataState('footerActive', overFooter);
      if (overFooter) { this.setDataState('tone', 'dark'); return; }
      const probe = headerRect.height / 2;
      let selected = null;
      document.querySelectorAll('[data-header-tone]').forEach((region) => {
        const rect = region.getBoundingClientRect();
        if (rect.top <= probe && rect.bottom > probe) selected = region;
      });
      if (selected && ['dark', 'light'].includes(selected.dataset.headerTone)) this.setDataState('tone', selected.dataset.headerTone);
    }
    updateImmersive() {
      const active = this.dataset.pageType === 'product' && Boolean(document.querySelector('marame-cross-sells[data-marame-immersive][data-immersive-active="true"]'));
      // Keep focused controls usable while their owning modal is open.
      const hidden = Boolean(active && !this.menu?.open && !this.contains(document.activeElement));
      this.setDataState('immersiveHidden', hidden);
      if (this.inert !== hidden) this.inert = hidden;
      if (hidden) {
        if (this.getAttribute('aria-hidden') !== 'true') this.setAttribute('aria-hidden', 'true');
      } else if (this.hasAttribute('aria-hidden')) this.removeAttribute('aria-hidden');
    }
    openMenu() {
      if (!this.menu || this.menu.open) return;
      this.closeSearch(false);
      this.ownsBodyLock = !document.body.classList.contains('overflow-hidden');
      this.menu.showModal();
      this.updateImmersive();
      document.body.classList.add('overflow-hidden');
      this.trigger?.setAttribute('aria-expanded', 'true');
      this.menu.querySelector('.marame-figma-menu__group summary')?.focus({ preventScroll: true });
    }
    closeMenu() {
      if (!this.menu?.open || this.menu.dataset.closing === 'true') return;
      this.menu.querySelectorAll('details-modal').forEach((modal) => { if (modal.isOpen?.()) modal.close(false); });
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { this.menu.close(); return; }
      this.menu.dataset.closing = 'true';
      const duration = window.matchMedia('(min-width: 1024px)').matches ? 220 : 600;
      this.closeTimer = setTimeout(() => { this.menu.close(); }, duration);
    }
    afterClose() {
      clearTimeout(this.closeTimer);
      delete this.menu.dataset.closing;
      this.trigger?.setAttribute('aria-expanded', 'false');
      if (this.ownsBodyLock) document.body.classList.remove('overflow-hidden');
      this.ownsBodyLock = false;
      this.trigger?.focus({ preventScroll: true });
      this.updateImmersive();
    }
    syncCartCount() {
      const count = this.querySelector('#cart-icon-bubble .cart-count-bubble > [aria-hidden=true]');
      if (count && /^\d+$/.test(count.textContent.trim()) && Number(count.textContent) > 9) count.textContent = '9+';
    }
    disconnectedCallback() {
      clearTimeout(this.closeTimer);
      this.controller?.abort();
      this.resize?.disconnect();
      this.cartObserver?.disconnect();
      this.tickerResize?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.tickerFrame) cancelAnimationFrame(this.tickerFrame);
      this.frame = 0;
      this.tickerFrame = 0;
      if (this.menu?.open) this.menu.close();
      if (this.ownsBodyLock) document.body.classList.remove('overflow-hidden');
      this.ownsBodyLock = false;
    }
  }
  customElements.define('marame-figma-header', MarameFigmaHeader);
})();

